import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Sidebar } from '@/components/common/Sidebar';
import { 
  Stethoscope, 
  ArrowRight, 
  AlertCircle,
  Clock,
  ChevronLeft,
  Video,
  Home
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { createPreference, PAYMENT_PRICING, prepareMercadoPagoWindow, launchMercadoPagoCheckout } from '@/services/payments';
import { formatBRLFromCents } from '@/lib/currency';
import { toast } from 'sonner';

const ON_DEMAND_PRICING = {
  TELEMEDICINE: PAYMENT_PRICING.ON_DEMAND_TELEMEDICINE_CENTS,
  HOME_CARE: PAYMENT_PRICING.ON_DEMAND_HOME_CARE_CENTS,
} as const;

export default function OnDemandTriagePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [reason, setReason] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [mode, setMode] = useState<'TELEMEDICINE' | 'HOME_CARE'>('TELEMEDICINE');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) {
      toast.error('Por favor, selecione um motivo para a consulta.');
      return;
    }

    setIsLoading(true);
    setError(null);

    const checkoutWindow = prepareMercadoPagoWindow();

    try {
      const paymentType = mode === 'TELEMEDICINE' ? 'ON_DEMAND_TELEMEDICINE' : 'ON_DEMAND_HOME_CARE';
      const prefResponse = await createPreference({ type: paymentType });

      if (!prefResponse.success || !prefResponse.data) {
        checkoutWindow?.close();
        const errorMsg = prefResponse.error || 'Não foi possível iniciar a preferência de pagamento.';
        setError(errorMsg);
        toast.error(errorMsg);
        setIsLoading(false);
        return;
      }

      sessionStorage.setItem(
        'pending_on_demand_triage',
        JSON.stringify({
          paymentId: prefResponse.data.paymentId,
          attendanceMode: mode,
          reason,
          symptoms,
        }),
      );

      launchMercadoPagoCheckout({
        checkoutWindow,
        initPoint: prefResponse.data.initPoint,
        paymentId: prefResponse.data.paymentId,
        goToResult: (path) => navigate(path),
      });
    } catch (err) {
      checkoutWindow?.close();
      console.error('Error starting on-demand payment:', err);
      const errorMsg = 'Erro ao iniciar pagamento. Tente novamente em instantes.';
      setError(errorMsg);
      toast.error(errorMsg);
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col">
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Voltar ao Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Consulta de Plantão
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Preencha os dados abaixo para entrar na fila de espera.
          </p>
        </header>

        <main className="flex-1 p-8 flex justify-center">
          <div className="w-full max-w-2xl">
            <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-100 dark:border-neutral-800 bg-teal-50/50 dark:bg-teal-900/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                    <Stethoscope className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Triagem Rápida</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Tempo médio de espera: ~5 min</p>
                  </div>
                </div>
              </div>

              {/* ─── ETAPA 1: Triagem (form próprio) ─── */}
              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3 text-red-800 dark:text-red-300">
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Como você deseja ser atendido? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setMode('TELEMEDICINE')}
                      className={cn(
                        "flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all",
                        mode === 'TELEMEDICINE' 
                          ? "border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400" 
                          : "border-gray-100 dark:border-neutral-800 text-gray-500 hover:border-gray-200 dark:hover:border-neutral-700"
                      )}
                    >
                      <Video className="w-8 h-8" />
                      <span className="font-semibold text-sm">Telemedicina</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('HOME_CARE')}
                      className={cn(
                        "flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all",
                        mode === 'HOME_CARE' 
                          ? "border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400" 
                          : "border-gray-100 dark:border-neutral-800 text-gray-500 hover:border-gray-200 dark:hover:border-neutral-700"
                      )}
                    >
                        <Home className="w-8 h-8" />
                        <span className="font-semibold text-sm">Domiciliar</span>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Motivo da Consulta <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-hidden transition-all"
                    >
                      <option value="">Selecione um motivo</option>
                      <option value="Dor de cabeça / Enxaqueca">Dor de cabeça / Enxaqueca</option>
                      <option value="Febre / Sintomas Gripais">Febre / Sintomas Gripais</option>
                      <option value="Dores Musculares">Dores Musculares</option>
                      <option value="Problemas Digestivos">Problemas Digestivos</option>
                      <option value="Renovação de Receita">Renovação de Receita</option>
                      <option value="Outros">Outros</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Descreva seus sintomas (Opcional)
                    </label>
                    <textarea
                      rows={4}
                      value={symptoms}
                      onChange={(e) => setSymptoms(e.target.value)}
                      placeholder="Ex: Sinto uma dor latente na região frontal há 2 dias..."
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-hidden transition-all resize-none"
                    />
                  </div>

                  <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                      <strong>Atenção:</strong> Esta modalidade é para casos de baixa complexidade. Em caso de emergência (dor no peito, falta de ar grave), procure um pronto-socorro imediatamente.
                    </p>
                  </div>

                  <div className="rounded-lg border border-teal-100 bg-teal-50/60 p-4 dark:border-teal-900/40 dark:bg-teal-900/10">
                    <p className="text-sm text-gray-600 dark:text-gray-300">Valor do atendimento imediato</p>
                    <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">
                      {formatBRLFromCents(ON_DEMAND_PRICING[mode])}
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !reason}
                    className="w-full py-4 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-xl font-bold text-lg shadow-lg shadow-teal-500/20 transition-all flex items-center justify-center gap-2 group"
                  >
                    {isLoading ? 'Redirecionando para Mercado Pago...' : 'Pagar com Mercado Pago'}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
            </div>
            
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-neutral-900 p-4 rounded-xl border border-gray-100 dark:border-neutral-800 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Espera</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Rápida</p>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-900 p-4 rounded-xl border border-gray-100 dark:border-neutral-800 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Médicos</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">De Plantão</p>
                </div>
              </div>
              <div className="bg-white dark:bg-neutral-900 p-4 rounded-xl border border-gray-100 dark:border-neutral-800 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Suporte</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">24 horas</p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
